import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, brl, STATUS_META, STATUS_ORDER } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Loader2, Search, History, Hash } from "lucide-react";
import { toast } from "sonner";

function monthRange(ym) {
  // ym = "2026-10" — bounds are in BRT (-03:00) so an order created at 23:00 BRT counts in the right month.
  const [y, m] = ym.split("-").map(Number);
  const pad = (n) => String(n).padStart(2, "0");
  const nextM = m === 12 ? 1 : m + 1;
  const nextY = m === 12 ? y + 1 : y;
  const from = `${y}-${pad(m)}-01T00:00:00-03:00`;
  const to = `${nextY}-${pad(nextM)}-01T00:00:00-03:00`;
  return { from, to };
}

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return "—"; }
}

export default function OwnerHistory() {
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [month, setMonth] = useState(defaultMonth);
  const [status, setStatus] = useState("todos");
  const [q, setQ] = useState("");
  const [data, setData] = useState({ items: [], summary: { count: 0, total: 0, total_approved: 0 } });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { from, to } = monthRange(month);
      const params = new URLSearchParams();
      params.set("from", from);
      params.set("to", to);
      if (status && status !== "todos") params.set("status", status);
      if (q.trim()) params.set("q", q.trim());
      const { data } = await api.get(`/orders/history?${params.toString()}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, status]);

  const onSearchSubmit = (e) => { e.preventDefault(); load(); };

  const items = data.items || [];
  const summary = data.summary || { count: 0, total: 0, total_approved: 0 };
  const monthLabel = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
  }, [month]);

  return (
    <div className="space-y-5 animate-fade-up">
      <div>
        <div className="text-[11px] font-mono-caps text-zinc-500">Histórico</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black uppercase text-zinc-100">Serviços realizados</h1>
        <p className="text-sm text-zinc-400 mt-1">Busque por número da OS, placa ou cliente, e veja o total do período.</p>
      </div>

      <form onSubmit={onSearchSubmit} className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            data-testid="history-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Nº da OS, placa ou cliente"
            className="pl-10 h-12 rounded-2xl bg-card2 border-card2-border text-zinc-100 placeholder:text-zinc-500"
          />
        </div>
        <Input
          data-testid="history-month"
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value || defaultMonth)}
          className="h-12 rounded-2xl bg-card2 border-card2-border text-zinc-100 sm:w-44"
        />
      </form>

      <div className="flex items-center gap-2 overflow-x-auto scrollbar-thin -mx-1 px-1">
        {[{ k: "todos", label: "Todos" }, ...STATUS_ORDER.map((k) => ({ k, label: STATUS_META[k].short }))].map(({ k, label }) => (
          <button
            key={k}
            type="button"
            data-testid={`history-filter-${k}`}
            onClick={() => setStatus(k)}
            className={`shrink-0 px-4 py-2 rounded-full text-xs font-semibold tracking-wide border transition-colors ${
              status === k
                ? "bg-green-brand text-zinc-950 border-green-brand"
                : "bg-card2 text-zinc-400 border-card2-border hover:text-zinc-100"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div data-testid="summary-count" className="rounded-2xl border border-card2-border bg-card2 p-3 sm:p-4">
          <div className="text-[10px] sm:text-[11px] font-mono-caps text-zinc-500">Ordens</div>
          <div className="font-display text-2xl sm:text-3xl font-black text-zinc-100 mt-1">{summary.count}</div>
          <div className="text-[10px] sm:text-[11px] text-zinc-500 mt-0.5 capitalize truncate">{monthLabel}</div>
        </div>
        <div data-testid="summary-total" className="rounded-2xl border border-card2-border bg-card2 p-3 sm:p-4">
          <div className="text-[10px] sm:text-[11px] font-mono-caps text-zinc-500">Total do mês</div>
          <div className="font-display text-xl sm:text-2xl font-black text-zinc-100 mt-1 break-all">{brl(summary.total)}</div>
          <div className="text-[10px] sm:text-[11px] text-zinc-500 mt-0.5">Todos os orçamentos</div>
        </div>
        <div data-testid="summary-approved" className="rounded-2xl border border-green-brand/40 bg-green-brand/5 p-3 sm:p-4">
          <div className="text-[10px] sm:text-[11px] font-mono-caps text-green-200">Aprovados / prontos</div>
          <div className="font-display text-xl sm:text-2xl font-black text-green-neon mt-1 break-all">{brl(summary.total_approved)}</div>
          <div className="text-[10px] sm:text-[11px] text-green-200/70 mt-0.5">Receita confirmada</div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 text-green-brand animate-spin" /></div>
      ) : items.length === 0 ? (
        <div data-testid="history-empty" className="rounded-2xl border border-dashed border-card2-border bg-card2/40 p-10 text-center">
          <History className="h-9 w-9 mx-auto text-zinc-700" />
          <p className="mt-3 text-zinc-400 font-medium">Nenhuma ordem encontrada neste período.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((o) => {
            const num = o.order_number != null ? String(o.order_number) : null;
            const plate = o.vehicle?.plate || "—";
            const model = [o.vehicle?.make, o.vehicle?.model].filter(Boolean).join(" ");
            return (
              <li key={o.id}>
                <Link
                  to={`/owner/ordens/${o.id}`}
                  data-testid={`history-row-${o.id}`}
                  className="flex items-center gap-3 rounded-2xl border border-card2-border bg-card2 hover:border-green-brand/60 transition-colors p-3 sm:p-4"
                >
                  <div className="h-11 w-11 rounded-2xl border border-green-brand/40 bg-green-brand/10 flex items-center justify-center shrink-0">
                    <Hash className="h-5 w-5 text-green-neon" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-display text-base font-bold text-zinc-100 truncate">
                          {num ? (
                            <>
                              <span className="text-green-neon">OS #{num}</span>
                              <span className="text-zinc-500"> · </span>
                            </>
                          ) : null}
                          <span>{plate}</span>
                        </div>
                        <div className="text-[11px] text-zinc-500 truncate" translate="no">
                          {o.customer_name || "—"}{model ? ` · ${model}` : ""} · {formatDate(o.created_at)}
                        </div>
                      </div>
                      <StatusBadge status={o.status} short />
                    </div>
                    {o.total != null && (
                      <div className="mt-1 text-sm font-bold text-zinc-100">{brl(o.total)}</div>
                    )}
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
